import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetDepenseByIdQuery } from 'actions/depenses/queries';
import HeaderFirst from 'utils/HeaderFirst';
import InputDetail from 'components/Inputs/InputDetail';
import useDepensesData from './useDepensesData';
import './client.css';
import FilePreview from 'components/FilePreview/FilePreview';
import { useGetProduitsQuery } from 'actions/produits/queries';
import { useGetCodesQuery } from 'actions/comptabilite/queries';
import AutocompleteSelect from 'components/AutocompleteSelect/AutocompleteSelect';

const formatDate = (dateString) => {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(dateString)
    .toLocaleDateString('fr-FR', options)
    .replace(',', '');
};

export default function DepenseDetail() {
  const { id } = useParams();
  const { id: depense_id } = useParams();
  const { clientID: dossier_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = new URLSearchParams(location.search).get('edit') === 'true';
  const {
    data: getDepense,
    error: depenseError,
    isLoading: isLoadingDepense,
  } = useGetDepenseByIdQuery({ id, dossier_id });
  const depenseDetail = getDepense?.data[0] || [];
  const { data: produitsData } = useGetProduitsQuery({ dossier_id });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossier_id });

  // Comptes proposables pour un achat : classe 6 (charges) + classe 2 (immobilisations)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => {
      const s = String(c.code ?? '');
      return s.startsWith('6') || s.startsWith('2');
    })
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const { handleUpdateDepense } = useDepensesData(depense_id);
  const [updateData, setUpdateData] = useState({
    date: '',
    intitule: '',
    description: '',
    quantite: '',
    prix: '',
    produits_associes: [],
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => {
    if (depenseDetail && isEditing) {
      // Assuming produitsData is already loaded and available
      const enrichedProduits = (depenseDetail.produits_associes || []).map(
        (prodAssoc) => {
          // Attempt to enrich each product with additional details if available
          const productDetail =
            produitsData?.data?.find((p) => p.id === prodAssoc.produit) || {};

          return {
            id: prodAssoc.produit, // Keep the product ID
            nom: productDetail.nom || prodAssoc.nom || 'Inconnu',
            quantite: prodAssoc.quantite?.toString() || '', // Convert quantite to string
            prix: prodAssoc.prix?.toString() || '', // Convert prix to string
            code_comptable: prodAssoc.code_comptable ?? null,
          };
        }
      );

      setUpdateData({
        date: depenseDetail.updated_at_at || '',
        intitule: depenseDetail.intitule || '',
        description: depenseDetail.description || '',
        produits_associes: enrichedProduits,
      });
    }
  }, [depenseDetail, isEditing, produitsData]);

  // Recalculer le total automatiquement quand les produits changent
  useEffect(() => {
    if (updateData.produits_associes && updateData.produits_associes.length > 0) {
      const total = updateData.produits_associes.reduce((sum, prod) => {
        const quantite = parseFloat(prod.quantite) || 0;
        const prix = parseFloat(prod.prix) || 0;
        return sum + (quantite * prix);
      }, 0);
      setCalculatedTotal(total);
    } else {
      setCalculatedTotal(0);
    }
  }, [updateData.produits_associes]);

  const handleQuantiteChange = (index, newQuantite) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].quantite = newQuantite.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handlePrixChange = (index, newPrix) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].prix = newPrix.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleProduitChange = (index, value) => {
    // Ne garder que les ids numeriques (selection dans la liste)
    if (typeof value !== 'number') return;
    const prod = (produitsData?.data || []).find((p) => p.id === value);
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        id: value,
        nom: prod?.nom || updatedProduits[index].nom,
        code_comptable:
          updatedProduits[index].code_comptable ?? prod?.code_comptable ?? null,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleCompteChange = (index, value) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        code_comptable: value,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const setDepenseDetail = async (e) => {
    e.preventDefault();
    const submitData = {
      ...updateData,
      montant_total: calculatedTotal, // Envoyer le total calculé
      produits_associes: updateData.produits_associes.map((prod) => {
        const code = String(prod.code_comptable ?? '').replace(/\D/g, '');
        return {
          produit: prod.id, // Assuming prod.id contains the product ID.
          quantite: parseFloat(prod.quantite), // Ensure quantite is a number.
          prix: prod.prix.toString(), // Ensure prix is a string.
          code_comptable: code ? Number(code) : null,
        };
      }),
    };

    // Call the update function with the prepared data.
    await handleUpdateDepense(id, submitData);
    navigate(-1); // Redirect after successful update.
  };

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">Modification de sortie: {depenseDetail?.id}</h4>
    </div>
  );

  if (isLoadingDepense) return <div>Chargement des détails de dépense...</div>;
  if (depenseError) return <div>Une erreur est survenue pour la dépense.</div>;

  return (
    <div className="depense-detail-container">
      <div className="d-flex align-items-center">
        <HeaderFirst
          nom={nom}
          description={
            <div>Modifiez les informations de cette sortie. Tous les calculs comptables seront mis à jour automatiquement.</div>
          }
        />
      </div>
      <form
        className="form-layout"
        onSubmit={setDepenseDetail}
        style={{ padding: '15px' }}
      >
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Fournisseur"
              name="fournisseur"
              disabled
              value={depenseDetail?.fournisseurs_details?.nom}
              onChange={(e) =>
                setDepenseDetail({
                  ...depenseDetail,
                  fournisseur: e.target.value,
                })
              }
              className={'custom-input'}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Date"
              name="date"
              type="text"
              disabled
              value={formatDate(depenseDetail.updated_at)}
              onChange={(e) =>
                setDepenseDetail({ ...depenseDetail, date: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
        </div>

        {/* Add other input fields as necessary */}
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Intitulé"
              name="intitule"
              value={updateData.intitule}
              onChange={(e) =>
                setUpdateData({ ...updateData, intitule: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Description"
              name="description"
              value={updateData.description}
              onChange={(e) =>
                setUpdateData({ ...updateData, description: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
        </div>
        {updateData.produits_associes &&
        updateData.produits_associes.length > 0 ? (
          updateData.produits_associes.map((prod, index) => (
            <div key={`${prod.id}-${index}`} className="row flex-row justify-content-between" style={{ marginBottom: '15px' }}>
              <div className="col-4">
                <AutocompleteSelect
                  label="Produit"
                  options={produitsData?.data || []}
                  value={prod.id}
                  onChange={(value) => handleProduitChange(index, value)}
                  placeholder="Sélectionnez un produit"
                />
              </div>
              <div className="col-2">
                <InputDetail
                  label="Quantité"
                  name={`quantite-${prod.id}`}
                  type="number"
                  value={prod.quantite}
                  onChange={(e) =>
                    handleQuantiteChange(index, parseFloat(e.target.value) || 0)
                  }
                  className={'custom-input'}
                />
              </div>
              <div className="col-3">
                <InputDetail
                  label="Prix"
                  name={`prix-${prod.id}`}
                  type="number"
                  value={prod.prix}
                  onChange={(e) => handlePrixChange(index, parseFloat(e.target.value) || 0)}
                  className={'custom-input'}
                />
              </div>
              <div className="col-3">
                <AutocompleteSelect
                  label="Compte comptable"
                  options={accountOptions}
                  value={prod.code_comptable ?? null}
                  onChange={(value) => handleCompteChange(index, value)}
                  createText="Utiliser"
                  onCreateNew={async (name) => {
                    const digits = String(name).replace(/\D/g, '');
                    if (!digits) return null;
                    return { id: Number(digits), nom: String(name).trim() };
                  }}
                  placeholder="Ex: 245000 (vide = compte du produit)"
                />
              </div>
            </div>
          ))
        ) : (
          <p>No products associated.</p>
        )}
        
        <div className="input-row d-flex">
          <div className="pieceContainer col-md-6">
            <p
              style={{ color: '#068F7A', fontWeight: 'bold' }}
              className="form-label"
            >
              Piece jointe
            </p>
            <Link to={depenseDetail?.piece_joint}>
              <div className="card py-3">
                <div className="card-body d-flex align-items-center justify-content-center text-center">
                  <FilePreview filePath={depenseDetail?.piece_joint} />
                </div>
              </div>
            </Link>
          </div>
          {depenseDetail?.justificatif_file && (
            <div className="pieceContainer col-md-6 ms-3">
              <p
                style={{ color: '#068F7A', fontWeight: 'bold' }}
                className="form-label"
              >
                Justificatif de règlement
              </p>
              <Link to={depenseDetail?.justificatif_file}>
                <div className="card py-3">
                  <div className="card-body d-flex align-items-center justify-content-center text-center">
                    <FilePreview filePath={depenseDetail?.justificatif_file} />
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>

        <div className="form-navigation-buttons">
          <button type="submit" className="next-button">
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
}
